import {
  TimeGologinComResponceType,
  checkProxy,
} from '@fd/common/api/checkProxy';
import { ProxyPoolProtocolType } from '@fd/common/biz/proxyPoolProtocolOptions';
import { ProxyServerType } from '@fd/common/biz/proxyType';
import clsx from 'clsx';
import { LucideCheckCheck, LucideRefreshCcwDot } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { linkClassName } from '../components/ui/styles';
import { Button } from '../components/ui/Button';
import useMobile from '../hooks/useMobile';

export type TestProxyStatus =
  | 'unknown'
  | 'checking'
  | 'success'
  | 'failed'
  | 'notraffic';

const iconCls = 'inline-block h-4 -mt-[1.5px]';

const testButtonCls = {
  unknown: 'text-indigo-600 hover:text-indigo-900',
  checking: 'text-indigo-600 font-semibold bg-indigo-600/10 sm:bg-transparent',
  success: 'text-green-700 bg-green-500/30 sm:bg-transparent text-center sm:text-right',
  failed: 'text-red-600 bg-rose-500/30 sm:bg-transparent',
  notraffic: 'font-semibold bg-indigo-600/10 sm:bg-transparent',
};

export const TestProxyButton = ({
  proxy,
  protocol,
}: {
  proxy: ProxyServerType;
  protocol: ProxyPoolProtocolType;
}) => {
  const isMobile = useMobile();
  const [checkStatus, setCheckStatus] = useState<TestProxyStatus>('unknown');
  const [metaInfo, setMetaInfo] = useState<TimeGologinComResponceType>();

  const testButtonContent = useMemo(
    () => ({
      unknown: (
        <>
          <LucideRefreshCcwDot className={iconCls} />
          Check proxy
        </>
      ),
      checking: (
        <>
          <LucideRefreshCcwDot className={clsx(iconCls, 'animate-spin')} />
          Checking<span className="sr-only">, {proxy.username}</span>
        </>
      ),
      success: (
        <span>
          <LucideCheckCheck className="inline-block h-4 -mt-[1.5px]" />
          {metaInfo?.country}, {metaInfo?.stateProv}, {metaInfo?.city}
          <br />
          {metaInfo?.ip}
        </span>
      ),
      failed: (
        <>
          <LucideRefreshCcwDot className={iconCls} />
          Proxy failed
        </>
      ),
      notraffic: (
        <>
          No traffic left.{' '}
          <NavLink
            to="/proxies/buy"
            target="_blank"
            rel="noopener"
            className={linkClassName}
          >
            Buy more
          </NavLink>
        </>
      ),
    }),
    [proxy, metaInfo]
  );

  return (
    <Button
      variant={isMobile && checkStatus === 'unknown' ? 'secondary' : 'ghost'}
      className={testButtonCls[checkStatus]}
      onClick={async () => {
        if (['checking', 'notraffic'].includes(checkStatus)) {
          return;
        }

        setCheckStatus('checking');
        try {
          const checkProxyResult = await checkProxy(proxy, protocol);
          if (checkProxyResult === 'notraffic') {
            setCheckStatus('notraffic');
          } else if (checkProxyResult === 'failed') {
            setCheckStatus('failed');
          } else {
            setMetaInfo(checkProxyResult);
            setCheckStatus('success');
          }
        } catch {
          setCheckStatus('failed');
        }
      }}
    >
      {testButtonContent[checkStatus]}
    </Button>
  );
};
